import axios from "axios";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { apiConfig } from "../../config";

export interface BillingLedgerEntry {
  _id: string;
  id: string;
  orgId: string;
  type: "proration" | "credit" | "recurring";
  amountCents: number;
  currency: string;
  description: string;
  status:
    | "pendingConfirmation"
    | "confirmed"
    | "cancelled"
    | "processed"
    | "open"
    | "failed"
    | "in_dispute"
    | "refunded";
  eventType:
    | "addSeat"
    | "removeSeat"
    | "tierUpgrade"
    | "tierDowngrade"
    | "addAddon"
    | "removeAddon"
    | "recurring"
    | "recurringCharge"
    | "recurringFailure"
    | "recurringCancellation";
  deltaSeats?: number;
  proposedTier?: string;
  currentMonthlyBillCents: number;
  newMonthlyBillCents: number;
  increaseCents: number;
  proratedAmountCents: number;
  processingFeeCents?: number;
  prorationFactor?: number;
  periodStart?: Date;
  periodEnd?: Date;
  effectiveDate?: Date;
  oldQuantity?: number;
  newQuantity?: number;
  oldUnitPriceCents?: number;
  newUnitPriceCents?: number;
  processor: "stripe" | "dimepay";
  processorChargeId?: string;
  processorInvoiceId?: string;
  processorPlanId?: string;
  reconciliationStatus: "unreconciled" | "reconciled" | "partial";
  eventTimestamps?: string[]; // Added this line
  timestamp: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface RunRenewalsResponse {
  processed: number;
  results: Array<{
    orgId: string;
    subscriptionId: string;
    status: "success" | "failed";
    error?: string;
  }>;
}

export interface ProcessPendingChargesResponse {
  processed: number;
  results: Array<{
    ledgerId: string;
    chargeId?: string;
    error?: boolean;
  }>;
}

export interface GenerateRecurringInvoicesResponse {
  created: number;
  skipped: number;
  errors: number;
  details: Array<{
    orgId: string;
    orgName: string;
    status: "created" | "skipped" | "error";
    message?: string;
  }>;
}

export interface SimulateDimepayWebhookRequest {
  eventType:
    | "recurring_payment_success"
    | "recurring_payment_failed"
    | "recurring_payment_cancelled";
  planId: string;
  chargeId?: string;
  amount?: number;
}

export interface SimulateDelinquencyRequest {
  orgId: string;
  daysPastDue: number;
}

export interface ApplyLateFeesResponse {
  processed: number;
  feesApplied: number;
  restricted: number;
}

export interface ProcessDueInvoicesResponse {
  processed: number;
  errors: number;
}

export interface ProcessPeriodEndJobResponse {
  success: boolean;
  processed: number;
  errors: number;
  details?: Array<{
    orgId: string;
    addonKey?: string;
    status: string;
    message: string;
  }>;
}

export interface CheckTrialExpirationsResponse {
  processed: number;
  restricted: number;
}

export interface UpdateSeatCountRequest {
  newSeatCount: number;
  effectiveDate?: string;
}

class BillingClient {
  private auth0Client: Auth0Client;

  constructor(auth0Client: Auth0Client) {
    this.auth0Client = auth0Client;
  }

  private async getAuthHeaders() {
    const token = await this.auth0Client.getTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Run renewals for all subscriptions that are due
   */
  async runRenewals(): Promise<RunRenewalsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/billing/renewals`,
      {},
      { headers },
    );
    return response.data;
  }

  /**
   * Generate recurring invoices (as if it's the 20th). For testing.
   * Idempotent: skips orgs that already have a recurring invoice for the next billing cycle.
   */
  async generateRecurringInvoices(
    orgIds?: string[],
  ): Promise<GenerateRecurringInvoicesResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/recurring-invoices/generate`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  /**
   * Process pending charges from the billing ledger
   */
  async processPendingCharges(
    currency: string = "usd",
  ): Promise<ProcessPendingChargesResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/subscription/v1/api/internal/billing/charges/process`,
      { currency },
      { headers },
    );
    return response.data;
  }

  /**
   * Simulate a Dimepay webhook event (for testing)
   */
  async simulateDimepayWebhook(
    payload: SimulateDimepayWebhookRequest,
  ): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/subscription/v1/api/internal/billing/webhooks/dimepay/simulate`,
      payload,
      { headers },
    );
    return response.data;
  }

  async simulateDelinquency(
    payload: SimulateDelinquencyRequest,
  ): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/simulate-delinquency`,
      payload,
      { headers },
    );
    return response.data;
  }

  async resetDelinquency(payload: { orgId: string }): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/reset-delinquency`,
      payload,
      { headers },
    );
    return response.data;
  }

  /**
   * Apply late fees and restrictions globally
   */
  async applyLateFees(orgIds?: string[]): Promise<ApplyLateFeesResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/apply-late-fees`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  /**
   * Process due invoices. Pass orgIds to scope; omit for all orgs.
   */
  async processDueInvoices(
    orgIds?: string[],
  ): Promise<ProcessDueInvoicesResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/process-due-invoices`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  /**
   * Check trial expirations. Pass orgIds to scope; omit for all orgs.
   */
  async checkTrialExpirations(
    orgIds?: string[],
  ): Promise<CheckTrialExpirationsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/check-trial-expirations`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  async processPeriodEndCancellations(
    orgIds?: string[],
  ): Promise<ProcessPeriodEndJobResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/process-period-end-cancellations`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  async processPeriodEndAddonRemovals(
    orgIds?: string[],
  ): Promise<ProcessPeriodEndJobResponse> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/internal/process-period-end-addon-removals`,
      orgIds?.length ? { orgIds } : {},
      { headers },
    );
    return response.data;
  }

  /**
   * Update seat count for a subscription
   */
  async updateSeatCount(
    auth0OrgId: string,
    request: UpdateSeatCountRequest,
  ): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/subscription/v1/api/internal/orgs/${auth0OrgId}/update-seats`,
      request,
      { headers },
    );
    return response.data;
  }

  /**
   * Get billing ledger entries for an organization
   */
  async getBillingLedger(
    auth0OrgId: string,
    options?: {
      type?: "proration" | "credit";
      status?: string | string[];
      limit?: number;
      offset?: number;
    },
  ): Promise<BillingLedgerEntry[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();

    if (options?.type) {
      params.append("type", options.type);
    }
    if (options?.status) {
      if (Array.isArray(options.status)) {
        options.status.forEach((s) => params.append("status", s));
      } else {
        params.append("status", options.status);
      }
    }
    if (options?.limit !== undefined) {
      params.append("limit", options.limit.toString());
    }
    if (options?.offset !== undefined) {
      params.append("offset", options.offset.toString());
    }

    const queryString = params.toString();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/org/${auth0OrgId}/current${
        queryString ? `?${queryString}` : ""
      }`,
      { headers },
    );
    return response.data;
  }

  /**
   * Get billing ledger entries for an organization in a time period (for Billing History page).
   */
  async getBillingLedgerByCycle(
    auth0OrgId: string,
    startTime: string,
    endTime: string,
  ): Promise<BillingLedgerEntry[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ startTime, endTime });
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/cycle/${auth0OrgId}?${params}`,
      { headers },
    );
    return response.data;
  }

  /**
   * Get all billing ledger entries for an organization (no cycle filter).
   */
  async getAllBillingLedger(auth0OrgId: string): Promise<BillingLedgerEntry[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/organization/${auth0OrgId}`,
      { headers },
    );
    return response.data;
  }

  async createSetupIntent(auth0OrgId: string): Promise<{
    processor?: "stripe" | "dimepay";
    clientSecret?: string;
    cardRequestToken?: string;
    cardUrl?: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/org/${auth0OrgId}/create-setup-intent`,
      {},
      { headers },
    );
    return response.data;
  }

  async processPayment(
    auth0OrgId: string,
    ledgerId: string,
    paymentMethodId: string,
  ): Promise<BillingLedgerEntry> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/org/${auth0OrgId}/process-payment`,
      { ledgerId, paymentMethodId },
      { headers },
    );
    return response.data;
  }

  async disputeLedger(
    ledgerId: string,
    reason: string,
    options?: { orgAuthId?: string; disputedBy?: string },
  ): Promise<BillingLedgerEntry> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/${ledgerId}/dispute`,
      { reason, ...options },
      { headers },
    );
    return response.data;
  }

  async getBillingLedgerEntryById(
    ledgerId: string,
  ): Promise<BillingLedgerEntry> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/${ledgerId}`,
      { headers },
    );
    return response.data;
  }

  async payAllInvoices(
    auth0OrgId: string,
    paymentMethodId: string,
  ): Promise<BillingLedgerEntry[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/org/${auth0OrgId}/pay-all`,
      { paymentMethodId },
      { headers },
    );
    return response.data;
  }

  async paySelectedInvoices(
    auth0OrgId: string,
    ledgerIds: string[],
    paymentMethodId: string,
  ): Promise<BillingLedgerEntry[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/billing-ledger/org/${auth0OrgId}/pay-selected`,
      { ledgerIds, paymentMethodId },
      { headers },
    );
    return response.data;
  }
}

export default BillingClient;
